// src/pages/RestaurantSite.tsx
import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getMenuPdfByLocalId } from '../../../crud/menu_api/menus'
import { getLocalConfigurationByLocalId } from '../../../crud/management_api/local_configurations'
import { getLocalSiteConfig } from '../../../crud/management_api/local_site_configuration'
import { getLocalByLocalId } from '../../../crud/management_api/locales'
import Carrousel from './components/Carrousel/Carrousel'
import MenuOnline from './components/MenuOnline/MenuOnline'
import WebViewer from '@pdftron/webviewer'
import SubscriptionModal from './components/SubscriptionModal/SubscriptionModal'
import { LocalSiteConfig, LocalSettings } from './types'
import LoadingSpinner from '../../../components/common/LoadingSpinner'
import './RestaurantSite.scss'
import { opensDirectMenu, directMenuViewerMessages } from '../../../configuration/directMenuRollout'
import Button from '../../../components/ui/Button/Button'

const RestaurantSite: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const local_id = id?.split('-').pop() || '0'
  const navigate = useNavigate()
  const lid = Number(local_id)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [local, setLocal] = useState<any>(null)
  const [config, setConfig] = useState<LocalSiteConfig | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [localSettings, setLocalSettings] = useState<LocalSettings | null>(null)
  const [view, setView] = useState<'landing' | 'carta'>('landing')
  const [pdfError, setPdfError] = useState(false)
  const [pdfRetry, setPdfRetry] = useState(0)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)

  const viewerRef = useRef<HTMLDivElement>(null)
  const directMenuApplied = useRef(false)

  // 1) Carga inicial de datos
  useEffect(() => {
    if (!lid) return
    let disposed = false
    setLoading(true)
    setPdfError(false)
    Promise.all([
      getLocalByLocalId(String(lid)),
      getLocalSiteConfig(lid),
      getMenuPdfByLocalId(lid),
      getLocalConfigurationByLocalId(lid),
    ])
    .then(([locRes, cfgRes, pdfRes, locSet]: any[]) => {
      if (disposed) return
      setLocal(locRes.data)
      const raw = cfgRes.data
      setConfig('object' in raw ? (raw.object as any) : (raw as any))
      setPdfUrl(pdfRes.data.menu_url ?? null)
      setLocalSettings(locSet.data)
      const siteConfig = 'object' in raw ? raw.object : raw
      const direct = opensDirectMenu(lid, raw, siteConfig, pdfRes.data.menu_url ?? null)
      if (direct) setView('carta')
      else if (directMenuApplied.current) setView('landing')
      directMenuApplied.current = direct
    })
    .catch(err => {
      if (disposed) return
      console.error('Error loading restaurant site:', err)
      setError(true)
    })
    .finally(() => { if (!disposed) setLoading(false) })
    return () => { disposed = true }
  }, [lid])

  // 2) Inicializa WebViewer y fuerza zoom al 100 %
// 2) Inicializa WebViewer y fuerza zoom al 100 %
  const directMenuReady = directMenuApplied.current && !loading
  useEffect(() => {
    if (directMenuApplied.current) {
      if (!directMenuReady || view !== 'carta' || !config?.show_pdf_menu || !pdfUrl || !viewerRef.current) return
      let disposed = false
      let release: (() => void) | undefined
      setPdfError(false)
      WebViewer({ path: `${process.env.PUBLIC_URL}/lib`, initialDoc: pdfUrl }, viewerRef.current)
        .then(instance => {
          const dispose = () => {
            void instance.UI.dispose().catch(() => console.error('feature_rollout.direct_menu.dispose_failed'))
          }
          if (disposed) { dispose(); return }
          const { documentViewer } = instance.Core
          const loaded = () => {
            if (!disposed) instance.UI.setFitMode(instance.UI.FitMode.FitWidth)
          }
          documentViewer.addEventListener('documentLoaded', loaded)
          release = () => {
            documentViewer.removeEventListener('documentLoaded', loaded)
            dispose()
          }
        })
        .catch(() => {
          if (!disposed) setPdfError(true)
        })
      return () => { disposed = true; release?.() }
    }
    if ((!directMenuApplied.current || directMenuReady) && config?.show_pdf_menu && pdfUrl && viewerRef.current) {
      viewerRef.current.innerHTML = ''
      const libPath = `${process.env.PUBLIC_URL}/lib`

      WebViewer(
        { path: libPath, initialDoc: pdfUrl },
        viewerRef.current as HTMLDivElement
      ).then(instance => {
        const { documentViewer } = instance.Core
        documentViewer.addEventListener('documentLoaded', () => {
          if (instance.UI.setFitMode && instance.UI.FitMode) {
            instance.UI.setFitMode(instance.UI.FitMode.FitWidth)
          } else {
            instance.UI.setZoomLevel('FitWidth')
          }
        })
      })
    }
  }, [view, config?.show_pdf_menu, pdfUrl, directMenuReady, pdfRetry]);

  // 3) Show subscription modal after 3 seconds on every page load
  useEffect(() => {
    if (!loading && local && config && view === 'landing') {
      const timer = setTimeout(() => {
        setShowSubscriptionModal(true);
      }, 3000); // Show after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [loading, local, config, view]);


  // Handlers de botones...
  const goToWeb = () => config?.site_url && window.open(config.site_url, '_blank')
  const goToReservas = () => navigate(`/Reservations/PlaceReservation/${lid}`)
  const goToCarta = () => setView('carta')
  const goToWhatsapp = () => {
    if (config?.site_phone) {
      const phone = config.site_phone.replace(/\D/g, '')
      window.open(`https://wa.me/${phone}`, '_blank')
    }
  }
  const verEnGoogle = () => {
  if (config?.place_id) {
    const googleMapsUrl = `https://www.google.com/maps/place/?q=place_id:${config.place_id}`
    window.open(googleMapsUrl, '_blank')
  }
}

  if (loading) return <div className="loading-container"><LoadingSpinner/></div>
  if (error || !local || !config) return <div className="error-message">Error al cargar la página</div>

  const renderLanding = () => (
    <>
      <Carrousel config={config}/>
      <div className="restaurant-site-buttons">
        {config.show_pdf_menu && pdfUrl && (
          <div className="option-btn" onClick={goToCarta}>
            <div className="option-icon">👨🏻‍🍳</div>
            <div className="option-content">
              <span className="option-title">Ver Carta</span>
              <span className="option-subtitle">Descubre nuestros platos</span>
            </div>
            <div className="option-arrow">→</div>
          </div>
        )}
        {!config.show_pdf_menu && config.show_menu && (
          <div className="option-btn" onClick={goToCarta}>
            <div className="option-icon">👨🏻‍🍳</div>
            <div className="option-content">
              <span className="option-title">Ver Carta</span>
              <span className="option-subtitle">Menú interactivo</span>
            </div>
            <div className="option-arrow">→</div>
          </div>
        )}
        {config.show_site && config.site_url && (
          <div className="option-btn" onClick={goToWeb}>
            <div className="option-icon">🌐</div>
            <div className="option-content">
              <span className="option-title">Página Web</span>
              <span className="option-subtitle">Conoce más sobre nosotros</span>
            </div>
            <div className="option-arrow">→</div>
          </div>
        )}
        {config.show_phone && config.site_phone && (
          <div className="option-btn" onClick={goToWhatsapp}>
            <div className="option-icon">📥</div>
            <div className="option-content">
              <span className="option-title">WhatsApp</span>
              <span className="option-subtitle">Contáctanos directamente</span>
            </div>
            <div className="option-arrow">→</div>
          </div>
        )}
        {config.show_coordinates && config.place_id && (
          <div className="option-btn" onClick={verEnGoogle}>
            <div className="option-icon">🗺️</div>
            <div className="option-content">
              <span className="option-title">Ver ubicación</span>
              <span className="option-subtitle">Abrir en Google Maps</span>
            </div>
            <div className="option-arrow">→</div>
          </div>
        )}
        {config.show_reservations && (
          <div className="option-btn" onClick={goToReservas}>
            <div className="option-icon">🗓️</div>
            <div className="option-content">
              <span className="option-title">Reservaciones</span>
              <span className="option-subtitle">Asegura tu lugar</span>
            </div>
            <div className="option-arrow">→</div>
          </div>
        )}
      </div>
    </>
  )

  return (
    <div className="restaurant-site-container">
      <div className="restaurant-site-header">
        {view === 'carta' && (
          <div className="volver-button" onClick={() => setView('landing')}>
            ←
          </div>
        )}
        <div className="restaurant-logo">
          <img
            src={localSettings?.image_url ?? `${process.env.PUBLIC_URL}/logos/Recurso 49MEDIOPUNTO.png`}
            alt="Restaurant Logo"
            className="restaurant-logo-image"
          />
        </div>
        <div className="title-container">
          <h1 className="restaurant-site-title">{local?.local_name || 'Restaurant'}</h1>
          {config?.show_description && ((config.local_description?.trim().length ?? 0) > 0) && (
            <p className="restaurant-description">{config.local_description}</p>
          )}
          <div className="scanlines" />
        </div>
      </div>      <div className="site-menu-card">
        {view === 'landing' && renderLanding()}
        {view === 'carta' && config.show_pdf_menu && pdfUrl && (
          <>
            {directMenuApplied.current && pdfError && <div role="alert">
              <p>{directMenuViewerMessages.unavailable}</p>
              <Button onClick={() => setPdfRetry(value => value + 1)}>{directMenuViewerMessages.retry}</Button>
            </div>}
            <div className="pdftron-viewer" ref={viewerRef} style={{ height: '80vh', width: '100%' }}/>
          </>
        )}
        {view === 'carta' && (!config.show_pdf_menu || !pdfUrl) && config.show_menu && (
          <MenuOnline local_id={lid} />
        )}
      </div>

      <div className="restaurant-site-powered">
        <span>Powered by</span>
        <img
          src={`${process.env.PUBLIC_URL}/logos/Recurso 49MEDIOPUNTO.png`}
          alt="MedioPunto"
          className="powered-logo"
        />
        <span>MedioPunto</span>
      </div>

      {/* Subscription Modal 
      <SubscriptionModal
        show={showSubscriptionModal}
        handleClose={() => setShowSubscriptionModal(false)}
        localId={lid}
        localName={local?.local_name || 'este restaurante'}
      />
      */}
    </div>
  )
}

export default RestaurantSite
